// Components
import MenuNavigationButton from '../../molecules/MenuNavigationButton/MenuNavigationButton';
import Picto from '../../atoms/Picto/Picto';

// Utils
import cn from '../../../utils/cn';

// Style
import style from './NavigationLeafList.module.css';

/**
 * @typedef {object} NavigationLeaf
 * @property {string} id
 * @property {string} label
 * @property {string | null} context - Secondary text (e.g. parent label)
 * @property {string} href
 * @property {boolean} [isHidden=false] - Hidden by the user: greyed with an eye-slash icon, still clickable
 * @property {boolean} [isForbidden=false] - No access: greyed with a lock icon, not clickable
 */

/**
 * NavigationLeafList component - Flat list of sidebar links (favorites, search results).
 * @param {object} params
 * @param {NavigationLeaf[]} params.leaves - Links to display
 * @param {(id: string) => void} params.onLeafClick - Click handler
 * @param {string} params.emptyLabel - Text displayed when the list is empty
 * @param {boolean} [params.isMenuOpen=false] - Whether the menu is open
 * @param {{ icon: import('../../atoms/Picto/Picto').AvailableIconName, label: string } | null} [params.action=null] - Optional button next to each link
 * @param {(id: string) => void} [params.onLeafAction] - Action button click handler
 * @param {string} [params.forbiddenLabel] - Tooltip of forbidden links
 * @returns {JSX.Element | null} NavigationLeafList component
 */
function NavigationLeafList({
  leaves,
  onLeafClick,
  emptyLabel,
  isMenuOpen = false,
  action = null,
  onLeafAction,
  forbiddenLabel,
}) {
  if (!isMenuOpen) return null;

  if (!leaves.length) {
    return <p className={style.empty}>{emptyLabel}</p>;
  }

  return (
    <div className={style['leaf-list']}>
      {leaves.map((leaf) => (
        <div key={leaf.id} className={cn([style.row, (leaf.isHidden || leaf.isForbidden) && style.hidden])}>
          <MenuNavigationButton
            id={`${leaf.id}-leaf-button`}
            theme="submenu"
            label={leaf.label}
            href={leaf.href}
            isMenuOpen={isMenuOpen}
            isDisabled={leaf.isForbidden}
            title={leaf.isForbidden && forbiddenLabel ? `${leaf.label} : ${forbiddenLabel}` : undefined}
            onClick={() => onLeafClick(leaf.id)}
          >
            {leaf.isForbidden ? <Picto icon="lock" className={style['hidden-icon']} /> : null}
            {leaf.isHidden && !leaf.isForbidden ? <Picto icon="eye-slash" className={style['hidden-icon']} /> : null}
            {leaf.context ? <span className={style.context}>{leaf.context}</span> : null}
          </MenuNavigationButton>
          {action ? (
            <button
              type="button"
              className={style.action}
              title={`${action.label} : ${leaf.label}`}
              aria-label={`${action.label} : ${leaf.label}`}
              onClick={() => onLeafAction?.(leaf.id)}
            >
              <Picto icon={action.icon} className={style['action-icon']} />
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export default NavigationLeafList;
